import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { StateContext } from '../stateContext';
import apiClient from "../utils/apiClient";
import { useNavigate } from 'react-router-dom';
import { useUser } from '../utils/UserProvider'; // Adjust the import path as necessary
import PresentationResultsModal from '../components/PresentationResultsModal';
import PricingPage from '../components/PricingPage';
import { getAllResults, deleteResultById } from '../utils/localDatabase';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline'


function UploadsPage() {
  const [uploads, setUploads] = useState(null);
  const {setToastData} = useContext(StateContext);
  const [isOpen, setIsOpen] = useState(false);
  const [downloadURL, setDownloadURL] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Deleting Upload")
  const [initiallyLoaded, setInitiallyLoaded] = useState(false);
  const [checkedUploads, setCheckedUploads] = useState(new Set());
  const [showPricing, setShowPricing] = useState(false);

  const { user, fetchUserProfile, profile } = useUser();
  const navigate = useNavigate();


 const handleCheckboxChange = (uploadId) => {
    setCheckedUploads((prevCheckedUploads) => {
      const updatedCheckedUploads = new Set(prevCheckedUploads);
      if (updatedCheckedUploads.has(uploadId)) {
        updatedCheckedUploads.delete(uploadId);
      } else {
        updatedCheckedUploads.add(uploadId);
      }
      return updatedCheckedUploads;
    });
  };

  async function checkUploadStatus(uploadID){
    try{

      const uploadStatusResponse = await apiClient.get(`/checkUploadStatus/${uploadID}`)
      // console.log(uploadStatusResponse.data)
      return (uploadStatusResponse.data.status)
    } catch(error){
      console.log(error.message)
      return ("upload failed")
    }
  }

  // useEffect(() => {
  //   const fetchTextFile = async () => {
  //     if (isOpen) {
  //       try {
  //         const response = await axios.get(downloadURL);
  //         setTextPreview(response.data);
  //       } catch (error) {
  //         console.error('Error fetching the text file:', error);
  //       }
  //     }
  //   };

  //   fetchTextFile();
  // }, [isOpen, downloadURL]);


const deleteSelectedUploads = async () => {
  setLoadingText("Deleting Upload");
  setIsLoading(true);

  const checkedUploadsArray = Array.from(checkedUploads);
  // console.log("Deleting uploads:", checkedUploadsArray);

  try {
    // Separate local and server uploads
    const localIds = checkedUploadsArray.filter((id) => id.startsWith("local-"));
    const serverIds = checkedUploadsArray.filter((id) => !id.startsWith("local-"));

    // Delete server uploads via API
    if (serverIds.length > 0) {
      const deleteUploadResponse = await apiClient.post("/deleteUploads", {
        uploads: serverIds,
        deleteUploads: true,
      });
      // console.log("Server delete response:", deleteUploadResponse.data);
    }

    // Delete local uploads from IndexedDB
    for (const id of localIds) {
      await deleteResultById(id);
      // console.log(`Deleted local upload ${id} from IndexedDB`);
    }

    // Refresh the uploads list to reflect deletions
    await getUploads();

    // Clear selection after deletion
    setCheckedUploads(new Set());
    setIsLoading(false);
  } catch (error) {
    console.error("Error deleting uploads:", error);
    setIsLoading(false);
  }
};


async function getUploads() {
  try {
    // console.log("Fetching uploads...");

    fetchUserProfile();

    // fetch server uploads
    const uploadsResponse = await apiClient.get("/getUploads");
    const serverUploads = uploadsResponse.data.uploads || [];

    // fetch local uploads
    const localResults = await getAllResults();
    // console.log("localResults", localResults);

    const localUploads = localResults.map((res) => ({
      id: res.id, // already prefixed with local-
      uploadName: res.fileName || "Local Upload",
      uploadedAt: res.uploadedAt || new Date(),
      imageUrl: res.data?.[0]?.thumb || "", 
      status: "processed",
      mediaContent: "numerical",
      isLocal: true,
    }));

    // merge lists
    const hasServer = Array.isArray(serverUploads) && serverUploads.length > 0;
    const hasLocal = Array.isArray(localUploads) && localUploads.length > 0;

    let mergedUploads = [];

    if (hasServer && hasLocal) {
      mergedUploads = [...serverUploads, ...localUploads];
    } else if (hasServer) {
      mergedUploads = serverUploads;
    } else if (hasLocal) {
      mergedUploads = localUploads;
    } else {
      mergedUploads = [];
    }

    // sort newest first
    mergedUploads.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));

    setUploads(mergedUploads);
    setInitiallyLoaded(true);
  } catch (error) {
    console.log(error);
  }
}


  async function handleUploadOpen(upload){
    const cutoffMs = Date.parse('2025-07-29T22:00:00-04:00'); 
    if (!upload) navigate('/upload')
    else if (upload?.status !== "processed") {
      const status = await checkUploadStatus(upload.id);
      if (status === "processed") {
        navigate(`/view-upload/${upload.id}`)
        upload.status = "processed"
        handleUploadOpen(upload)
      }
      else setToastData({text:"Still processing", show:true})
      return
    }
    else if (Date.parse(upload.uploadedAt) > cutoffMs) navigate(`/view-upload/${upload.id}`);
    else if (upload?.mediaContent === "presentation"){
      setLoadingText("Loading Presentation Results")
      setIsLoading(true)
      try {

        const response = await apiClient.get(`/getUploadDetailsOld/${upload.id}`);
        // console.log({response})
        const resultsURL = response?.data?.results?.resultsUrl
        // console.log({resultsURL})
        if(resultsURL) {
          setDownloadURL(resultsURL)
          setIsOpen(true)
        }
      } catch {  
        setToastData({text:"Results not yet available.", show:true})
      } finally {
      setIsLoading(false)
      }

    }
    else navigate(`/view-upload-numerical/${upload.id}`)
  }

  useEffect(() => {
    if (!uploads && user) getUploads()
  }, [user]);

  return (
<div className="w-3/4 mx-auto p-4 border rounded-lg shadow-lg">
  {isLoading && 
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex flex-col justify-center items-center">
    <div className="bg-neutral p-4 rounded-lg flex flex-col items-center justify-center space-y-2">
      <span className="loading loading-spinner loading-lg text-white"></span>
      <p className="text-white text-lg">{loadingText}{loadingText === "Deleting Upload" && checkedUploads.size > 1 ? "s" : ""}</p>
    </div>
  </div>}
  {showPricing && (
    <div className="modal modal-open wax-w-full">
      <div className="modal-box flex flex-col items-center gap-2 w-11/12 max-w-full ">
      <div className="flex flex-col items-center gap-4">
        <p className="text-2xl font-bold">Purchase more credits</p>
        <p className="italic text-sm text-center">Credits can be mixed and matched across processing types.</p>
        <div className="stats shadow">
          <div className="stat">
            <div className="stat-title">Credit Balance:</div>
            <div className="stat-value">{profile?.credits || 0}</div>
            <div className="stat-desc">credits</div>
          </div>
        </div>
      </div>
        <PricingPage/>
        <div className="modal-action w-full text-right">
          <button className="btn" onClick={() => setShowPricing(false)}>Close</button>
        </div>
      </div>
    </div>)}
  {isOpen && <PresentationResultsModal setIsOpen={setIsOpen} downloadURL={downloadURL} setToastData={setToastData}/>}
  <h2 className="text-2xl font-semibold text-center">Uploads</h2>
  <p className="text-center mb-4">Uploads are deleted after 30 days.</p>
  {profile?.credits >= 0 && 
    <div className="flex flex-col justify-center mx-auto items-center pb-4">
      <div className="stats shadow">
      <div className="stat">
        <div className="stat-title">Credit Balance:</div>
        <div className="stat-value">{profile?.credits}</div>
        <div className="stat-desc">credits</div>
      <button className="btn btn-sm mt-2" onClick={()=>setShowPricing(true)}>Purchase Credits</button>  
      </div>
      </div>
    </div>}
  <div className="flex justify-center w-full pb-4 gap-4">
    <button className="btn btn-success" onClick={() => handleUploadOpen()}>New Upload</button>
    <button className="btn btn-primary btn-outline" onClick={() => navigate('/local')}>New Local Upload</button>
  </div>
  <div className="flex flex-wrap justify-center gap-4">
     {/* {!isLoading && <div
      className="flex justify-center items-center w-18 h-18 p-8 bg-gray-200 rounded-lg cursor-pointer hover:bg-gray-300"
      onClick={() => handleUploadOpen()} // Replace navigateToUploadPage with your actual navigation function
      title="Uploads"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    </div>} */}
    {!initiallyLoaded ? (
      <>
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className=" p-2 rounded-lg w-52 h-52">
            <div className="h-32 bg-gray-300 rounded-lg skeleton"></div>
            <div className="mt-2 space-y-2">
              <div className="h-4 bg-gray-300 rounded skeleton"></div>
              <div className="h-4 bg-gray-300 rounded skeleton"></div>
              <div className="h-4 bg-gray-300 rounded skeleton"></div>
            </div>
          </div>
        ))}
      </>
    ) : uploads && uploads.length > 0 ? (
      uploads.map((upload) => (
        <div key={upload.id} className="flex items-center gap-2 border p-2 rounded-lg w-full sm:w-auto sm:max-w-sm ">
          <img
            src={upload.imageUrl}
            alt={upload.name}
            title="Click to open upload"
            className="w-32 h-32 object-cover rounded-lg cursor-pointer"
            onClick={() => handleUploadOpen(upload)}
          />
          <div className="flex flex-col">
            <p className="text-lg font-medium">{upload.uploadName}</p>
            <p className="text-sm text-gray-500">{new Date(upload.uploadedAt).toLocaleString().split(", ")[0]}</p>
            <p className="text-sm text-gray-500">{new Date(upload.uploadedAt).toLocaleString().split(", ")[1]}</p>
            {upload.isLocal &&<p className="text-sm text-gray-500">
               Local Upload
            </p>}
            {upload.status === 'uploaded' && <div className="badge badge-secondary mb-2">Processing</div>}
            {upload.status === 'failed' && <div className="badge badge-error mb-2">Failed</div>}
            {/* {upload?.mediaContent !== "numerical" && <p className="text-xs text-gray-500">{upload.mediaContent}</p>} */}
            <label className="cursor-pointer label">
              <input
                type="checkbox"
                className="checkbox checkbox-normal"
                checked={checkedUploads.has(upload.id)}
                onChange={() => handleCheckboxChange(upload.id)}
              />
            </label>
          </div>
          
        </div>
      ))
    ) : (
      <div className="text-center text-lg w-full">No uploads yet</div>
    )}
   
  </div>
  {uploads && uploads.length > 0 && <button className={`mt-4 btn w-full sm:w-auto ${Array.from(checkedUploads).length > 0 ? "btn-error" : "btn-disabled"}`} onClick={deleteSelectedUploads}>Delete Selected</button>}
</div>

  );
}

export default UploadsPage;
